import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import React, { FC, useId, useState } from 'react';
import styled from 'styled-components';
import {
  OvulatoryEventLoadStatus,
  OvulatoryEventTableDay,
} from '../../../common/model/type/ovulatory-event-prediction.type';
import { HORMONE_SERIES_ORDER } from '../../../common/utils/services/hormone-trends-utils';
import {
  formatOvulatoryEventDay,
  formatOvulatoryEventScore,
  getOvulatoryEventWrongKind,
} from '../../../common/utils/services/ovulatory-event-prediction-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import {
  OVULATORY_EVENT_CYCLE_PHASE_LABELS,
  OVULATORY_EVENT_EVIDENCE_LABELS,
  OVULATORY_EVENT_LIMITED_EVIDENCE_DESCRIPTION,
  OVULATORY_EVENT_NO_EVIDENCE_LABEL,
  OVULATORY_EVENT_OUTCOME_DESCRIPTIONS,
  OVULATORY_EVENT_OUTCOME_LABELS,
  OVULATORY_EVENT_STATE_SHORT_LABELS,
  OVULATORY_EVENT_STATE_TEXT_COLOURS,
  OVULATORY_EVENT_TTC_STATUS_LABELS,
  OVULATORY_EVENT_WRONG_LABELS,
} from '../organisms/ovulatory-event-prediction-presentation';
import OvOvulatoryEventDayDetails from './OvOvulatoryEventDayDetails';
import OvOvulatoryEventRuleValue from './OvOvulatoryEventRuleValue';

interface OvOvulatoryEventDayTableRowProps {
  day: OvulatoryEventTableDay;
  detailsStatus: OvulatoryEventLoadStatus;
  scansStatus: OvulatoryEventLoadStatus;
  columnCount: number;
}

const PLACEHOLDERS: Record<OvulatoryEventLoadStatus, string> = {
  loading: '…',
  error: 'n/a',
  ready: '—',
};

const OvOvulatoryEventDayTableRow: FC<OvOvulatoryEventDayTableRowProps> = ({
  day: { row, scans },
  detailsStatus,
  scansStatus,
  columnCount,
}) => {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const scanCells = HORMONE_SERIES_ORDER.map((seriesId) => {
    const value = scansStatus === 'ready' ? scans?.[seriesId] : undefined;

    return (
      <NumberCell key={seriesId} data-testid={`scan-${seriesId}`}>
        {value == null ? (
          <Muted>{PLACEHOLDERS[scansStatus]}</Muted>
        ) : (
          value.toFixed(2)
        )}
      </NumberCell>
    );
  });

  const { comparison, prediction } = row;
  const { day, model, rule, observed_outcome, observed_event_day } = comparison;
  const dayLabel = formatOvulatoryEventDay(day, 'MMM dd, yyyy');
  const wrongKind = getOvulatoryEventWrongKind(comparison);
  const approximate = prediction?.backfilled ?? false;
  const ttcStatusLabel = rule.ttc_status
    ? OVULATORY_EVENT_TTC_STATUS_LABELS[rule.ttc_status] ?? rule.ttc_status
    : null;
  const cyclePhaseLabel = rule.cycle_phase
    ? OVULATORY_EVENT_CYCLE_PHASE_LABELS[rule.cycle_phase] ?? rule.cycle_phase
    : null;

  return (
    <>
      <Row
        $wrong={wrongKind !== null}
        data-testid="day-table-row"
        data-wrong={wrongKind !== null}
      >
        <td>
          <ExpandButton
            type="button"
            aria-expanded={expanded}
            aria-controls={detailsId}
            aria-label={`${expanded ? 'Hide' : 'Show'} details for ${dayLabel}`}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </ExpandButton>
        </td>
        <DayCell scope="row">{dayLabel}</DayCell>
        {scanCells}
        <td style={{ color: OVULATORY_EVENT_STATE_TEXT_COLOURS[model.state] }}>
          {OVULATORY_EVENT_STATE_SHORT_LABELS[model.state]}
        </td>
        <ScoreCell>
          {model.score !== null && model.threshold !== null
            ? `${formatOvulatoryEventScore(
                model.score
              )} / ${formatOvulatoryEventScore(model.threshold)}`
            : 'No score'}
        </ScoreCell>
        <td>
          {model.evidence_level === 'limited' ? (
            <span title={OVULATORY_EVENT_LIMITED_EVIDENCE_DESCRIPTION}>
              ! {OVULATORY_EVENT_EVIDENCE_LABELS.limited}
            </span>
          ) : (
            (model.evidence_level &&
              OVULATORY_EVENT_EVIDENCE_LABELS[model.evidence_level]) ??
            OVULATORY_EVENT_NO_EVIDENCE_LABEL
          )}
        </td>
        <td>
          {detailsStatus === 'loading' ? (
            <Muted>{PLACEHOLDERS.loading}</Muted>
          ) : (
            <>
              <OvOvulatoryEventRuleValue
                prefix="TTC"
                label={ttcStatusLabel}
                approximate={approximate}
              />
              <Secondary>
                <OvOvulatoryEventRuleValue
                  prefix="Phase"
                  label={cyclePhaseLabel}
                  approximate={approximate}
                />
              </Secondary>
            </>
          )}
        </td>
        <td title={OVULATORY_EVENT_OUTCOME_DESCRIPTIONS[observed_outcome]}>
          {OVULATORY_EVENT_OUTCOME_LABELS[observed_outcome]}
          {observed_event_day && (
            <Secondary>
              Event {formatOvulatoryEventDay(observed_event_day)}
            </Secondary>
          )}
        </td>
        <ReviewCell>
          {wrongKind && OVULATORY_EVENT_WRONG_LABELS[wrongKind]}
        </ReviewCell>
      </Row>
      {expanded && (
        <tr>
          <DetailsCell id={detailsId} colSpan={columnCount}>
            <OvOvulatoryEventDayDetails status={detailsStatus} row={row} />
          </DetailsCell>
        </tr>
      )}
    </>
  );
};

export default OvOvulatoryEventDayTableRow;

const Row = styled.tr<{ $wrong: boolean }>`
  background: ${({ $wrong }) =>
    $wrong ? Colours.STRAWBERRY['50'] : 'transparent'};
  box-shadow: ${({ $wrong }) =>
    $wrong ? `inset 3px 0 0 ${Colours.OV_ACCENT_MAGENTA}` : 'none'};
`;

const DayCell = styled.th`
  font-weight: normal;
  text-align: left;
  white-space: nowrap;
`;

const NumberCell = styled.td`
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
`;

const ScoreCell = styled.td`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const Muted = styled.span`
  color: ${Colours.MEDIUM_GRAY_BLUE};
`;

const Secondary = styled.span`
  display: block;
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
`;

const ReviewCell = styled.td`
  color: ${Colours.OV_ACCENT_MAGENTA};
  font-size: ${Variables.fontSizes.SMALL};
  white-space: nowrap;
`;

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: 0;
  border-radius: ${Variables.borderRadius.CIRCLE};
  background: transparent;
  color: ${Colours.OV_BASE};
  cursor: pointer;

  svg {
    font-size: 1.125rem;
  }

  &:hover {
    background: ${Colours.OV_WHITE_HOVER};
  }

  &:focus-visible {
    outline: 2px solid ${Colours.OV_ACCENT_BLUE};
    outline-offset: 1px;
  }
`;

const DetailsCell = styled.td`
  && {
    padding: 0.5rem 0.75rem 1rem;
  }
`;
